import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { useAuth } from '../../../hooks/useAuth';
import {
  listSprints,
  createSprint,
  updateSprint,
  completeSprint,
  type Sprint,
  SPRINT_STATUS_COLORS,
} from '../../../lib/tasks';

export const Route = createFileRoute('/kinerja/tasks/projects/$projectId')({
  component: ProjectPage,
});

function ProjectPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <ProjectContent />
    </ProtectedRoute>
  );
}

function ProjectContent() {
  const { projectId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role && ['admin', 'hr', 'manager', 'super_admin'].includes(user.role);

  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newSprint, setNewSprint] = useState({ name: '', goal: '', startDate: '', endDate: '' });
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState<string | null>(null);

  const fetchSprints = async () => {
    setLoading(true);
    try {
      const data = await listSprints(projectId);
      setSprints(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSprints(); }, [projectId]);

  const handleCreateSprint = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createSprint(projectId, {
        name: newSprint.name,
        goal: newSprint.goal || undefined,
        startDate: newSprint.startDate || undefined,
        endDate: newSprint.endDate || undefined,
      });
      setNewSprint({ name: '', goal: '', startDate: '', endDate: '' });
      setShowNew(false);
      fetchSprints();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create sprint');
    } finally {
      setCreating(false);
    }
  };

  const handleActivate = async (sprintId: string) => {
    try {
      await updateSprint(sprintId, { status: 'active' });
      fetchSprints();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  const handleComplete = async (sprintId: string) => {
    if (!confirm('Complete this sprint? Metrics will be recorded for all assignees.')) return;
    setCompleting(sprintId);
    try {
      await completeSprint(sprintId);
      fetchSprints();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setCompleting(null);
    }
  };

  const activeSprints = sprints.filter((s) => s.status === 'active');
  const planningSprints = sprints.filter((s) => s.status === 'planning');
  const completedSprints = sprints.filter((s) => s.status === 'completed');

  const SprintCard = ({ sprint }: { sprint: Sprint }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:border-teal-300 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{sprint.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SPRINT_STATUS_COLORS[sprint.status]}`}>
              {sprint.status}
            </span>
          </div>
          {sprint.goal && <p className="text-sm text-gray-500 mt-1">{sprint.goal}</p>}
          {(sprint.startDate || sprint.endDate) && (
            <p className="text-xs text-gray-400 mt-1.5">
              {sprint.startDate ? new Date(sprint.startDate).toLocaleDateString() : '?'}
              {' — '}
              {sprint.endDate ? new Date(sprint.endDate).toLocaleDateString() : '?'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 ml-3">
          {sprint.status === 'completed' ? (
            <button
              onClick={() => navigate({ to: '/kinerja/tasks/sprints/$sprintId/summary' as any, params: { sprintId: sprint.id } as any })}
              className="px-3 py-1.5 text-sm text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 font-medium"
            >
              View Summary
            </button>
          ) : (
            <button
              onClick={() => navigate({ to: '/kinerja/tasks/sprints/$sprintId' as any, params: { sprintId: sprint.id } as any, search: { projectId } as any })}
              className="px-3 py-1.5 text-sm text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 font-medium"
            >
              Open Board
            </button>
          )}
          {canManage && sprint.status === 'planning' && (
            <button
              onClick={() => handleActivate(sprint.id)}
              className="px-3 py-1.5 text-sm text-white bg-teal-600 rounded-lg hover:bg-teal-700 font-medium"
            >
              Start
            </button>
          )}
          {canManage && sprint.status === 'active' && (
            <button
              onClick={() => handleComplete(sprint.id)}
              disabled={completing === sprint.id}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              {completing === sprint.id ? 'Completing...' : 'Complete Sprint'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate({ to: '/kinerja/tasks/' })}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Sprints</h1>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{projectId}</p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowNew(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
          >
            + New Sprint
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* New sprint form */}
      {showNew && (
        <form
          onSubmit={handleCreateSprint}
          className="mb-6 bg-white rounded-xl border border-teal-200 p-5 space-y-3"
        >
          <h3 className="font-medium text-gray-900">New Sprint</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Sprint Name</label>
              <input
                type="text"
                value={newSprint.name}
                onChange={(e) => setNewSprint((s) => ({ ...s, name: e.target.value }))}
                placeholder="Sprint 1"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Sprint Goal (optional)</label>
              <input
                type="text"
                value={newSprint.goal}
                onChange={(e) => setNewSprint((s) => ({ ...s, goal: e.target.value }))}
                placeholder="Ship the login feature"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                value={newSprint.startDate}
                onChange={(e) => setNewSprint((s) => ({ ...s, startDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={newSprint.endDate}
                onChange={(e) => setNewSprint((s) => ({ ...s, endDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowNew(false)}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Sprint'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
        </div>
      ) : sprints.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p className="font-medium">No sprints yet</p>
          {canManage && <p className="text-sm mt-1">Create the first sprint to start tracking tasks.</p>}
        </div>
      ) : (
        <div className="space-y-6">
          {activeSprints.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Active</h2>
              <div className="space-y-3">{activeSprints.map((s) => <SprintCard key={s.id} sprint={s} />)}</div>
            </div>
          )}
          {planningSprints.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Planning</h2>
              <div className="space-y-3">{planningSprints.map((s) => <SprintCard key={s.id} sprint={s} />)}</div>
            </div>
          )}
          {completedSprints.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Completed</h2>
              <div className="space-y-3">{completedSprints.map((s) => <SprintCard key={s.id} sprint={s} />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
